import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { NotificationMessage } from "../shared/models/models";
import { Login, PinLogin } from "./models/AccountModels";
import { SerializedError } from "../shared/models/errorHandlingModels";
import { login, pinLogin } from "./api/accountApi";
import accountContext from "./accountContext";
import { serializeError, createErrorNotification } from "../shared/utils/errorUtils";

interface LoginState {
  isLoading: boolean;
  formNotificationMessage?: NotificationMessage;
  showPassword: boolean;
  isError: boolean;
}

const initialState: LoginState = {
  isLoading: false,
  formNotificationMessage: undefined,
  showPassword: false,
  isError: false,
};

export const handleLogin = createAsyncThunk(
  "login/handleLogin",
  async (model: Login, { rejectWithValue }) => {
    try {
      const loginResult = await login(model);
      if (!loginResult) {
        return rejectWithValue(serializeError({ message: "Login failed" }));
      }

      accountContext.finishLogin(loginResult);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const handlePinLogin = createAsyncThunk(
  "login/handlePinLogin",
  async (model: PinLogin, { rejectWithValue }) => {
    try {
      const loginResult = await pinLogin(model);
      if (!loginResult) {
        return rejectWithValue(serializeError({ message: "PIN code is invalid" }));
      }

      accountContext.finishLogin(loginResult);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    togglePasswordVisibility: (state) => {
      state.showPassword = !state.showPassword;
    },
    resetLoginState: (state) => {
      state.isLoading = false;
      state.formNotificationMessage = undefined;
      state.showPassword = false;
    },
    setFormNotificationMessage: (state, action: PayloadAction<NotificationMessage | undefined>) => {
      state.formNotificationMessage = action.payload;
    },
    clearPinError: (state) => {
      state.isError = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(handleLogin.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = undefined;
      })
      .addCase(handleLogin.fulfilled, (state) => {
        state.isLoading = false;
        state.formNotificationMessage = undefined;
      })
      .addCase(handleLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.formNotificationMessage = createErrorNotification(action.payload as SerializedError);
      })
      .addCase(handlePinLogin.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(handlePinLogin.fulfilled, (state) => {
        state.isLoading = false;
        state.formNotificationMessage = undefined;
        state.isError = false;
      })
      .addCase(handlePinLogin.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      });
  },
});

export const {
  togglePasswordVisibility,
  resetLoginState,
  setFormNotificationMessage,
  clearPinError,
} = loginSlice.actions;

export default loginSlice.reducer; 